import { INSIGHTS_DATE_OPTIONS, useConfigQuery } from '@buildwise/libs/configurations';
import useMultiConfigEntryOptionsQuery from '@buildwise/libs/configurations/queries/useMultiConfigEntryOptionsQuery';
import { ClearFiltersOptions, FilterType } from 'components/common/filter/Filter';
import { ConfigEntryFieldType, ConfigEntryRefFuncName } from 'config/config';
import useUserData from 'hooks/useUserData';
import { compact, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type GetAdditionalFiltersOptions = {
  prevState?: FilterType[];
  configFilters: FilterType[];
};

function useConfigFilters({
  companyId,
  configName,
  data = [],
  shouldFetch = true,
  showFiltersCount = false,
  showSelectAll = true,
  hideConfigFiltersList = [],
  getAdditionalFilters,
}: {
  companyId?: string;
  configName: string;
  data?: Record<string, any>[];
  shouldFetch?: boolean;
  showFiltersCount?: boolean;
  showSelectAll?: boolean;
  hideConfigFiltersList?: string[];
  getAdditionalFilters?: (options: GetAdditionalFiltersOptions) => FilterType[];
}) {
  const { user } = useUserData();
  const { activeUniqueEntries: _filtersEntries, isLoading } = useConfigQuery({
    companyId,
    configName,
    shouldFetch,
  });

  // Filters out entries from _filtersEntries based on hideConfigFiltersList.
  // NOTE: DO NOT use hideConfigFiltersList as dependency it will lead to infinite loop and right now
  // it is not needed because it's a constant value
  const filtersEntries = useMemo(() => {
    if (!hideConfigFiltersList.length) return _filtersEntries;
    return _filtersEntries.filter(({ entryCode }) => !hideConfigFiltersList.includes(entryCode));
  }, [_filtersEntries]);

  // NOTE: DO NOT use configsValues as a dependency in useEffect. Please read the reason in query.
  const { configsValues, isLoadingQueries: isFiltersLoading } = useMultiConfigEntryOptionsQuery({
    companyId,
    configEntries: filtersEntries.map((configEntry) => ({
      configEntry,
    })),
    shouldFetch: filtersEntries.length > 0 && shouldFetch,
  });
  const configsOptions = useMemo(
    () => (configsValues ? JSON.stringify(configsValues) : ''),
    [configsValues],
  );
  // Consolidate all filter values - config values & custom values if any from data. for ex: custom job title values.
  const filtersWithOptionsAndCounts = useMemo(() => {
    const filtersWithOptionsAndCounts: Record<
      string,
      {
        options: { label: string; value: string }[];
        optionsWithCount: Record<string, number>;
        values: string[];
      }
    > = {};
    Object.keys(configsValues).forEach((filterName) => {
      filtersWithOptionsAndCounts[filterName] = {
        options: configsValues[filterName]?.activeUniqueOrderedOptions || [],
        values: (configsValues[filterName]?.activeUniqueOrderedOptions || []).map(
          (option) => option.value,
        ),
        optionsWithCount: {},
      };
    });
    data.forEach((item) => {
      Object.keys(filtersWithOptionsAndCounts).forEach((filterName) => {
        if (!item[filterName]) {
          if (!filtersWithOptionsAndCounts[filterName].values.includes('(blank)')) {
            // Add (blank) option
            filtersWithOptionsAndCounts[filterName].values.push('(blank)');
            filtersWithOptionsAndCounts[filterName].options.push({
              label: '(blank)',
              value: '(blank)',
            });
          }
          filtersWithOptionsAndCounts[filterName].optionsWithCount['(blank)'] =
            filtersWithOptionsAndCounts[filterName].optionsWithCount['(blank)']
              ? filtersWithOptionsAndCounts[filterName].optionsWithCount['(blank)'] + 1
              : 1;
        }
        // check string or array value
        else if (Array.isArray(item[filterName])) {
          // Add option
          filtersWithOptionsAndCounts[filterName].options = uniqBy<{
            label: string;
            value: string;
          }>(
            [
              ...filtersWithOptionsAndCounts[filterName].options,
              ...item[filterName].map((value) => ({ label: value, value })),
            ],
            (option) =>
              typeof option.value === 'string' ? option.value.toLowerCase() : option.value,
          );
          filtersWithOptionsAndCounts[filterName].values = uniqBy<string>(
            [...filtersWithOptionsAndCounts[filterName].values, ...item[filterName]],
            (value) => (typeof value === 'string' ? value.toLowerCase() : value),
          );
          // Update count
          item[filterName].forEach((option) => {
            filtersWithOptionsAndCounts[filterName].optionsWithCount[option] =
              filtersWithOptionsAndCounts[filterName].optionsWithCount[option]
                ? filtersWithOptionsAndCounts[filterName].optionsWithCount[option] + 1
                : 1;
          });
        } else {
          // Check if option doesn't exist in entries
          if (!filtersWithOptionsAndCounts[filterName].values.includes(item[filterName])) {
            filtersWithOptionsAndCounts[filterName].options.push({
              label: item[filterName],
              value: item[filterName],
            });
            filtersWithOptionsAndCounts[filterName].values.push(item[filterName]);
          }
          // Update count
          filtersWithOptionsAndCounts[filterName].optionsWithCount[item[filterName]] =
            filtersWithOptionsAndCounts[filterName].optionsWithCount[item[filterName]]
              ? filtersWithOptionsAndCounts[filterName].optionsWithCount[item[filterName]] + 1
              : 1;
        }
      });
    });

    return filtersWithOptionsAndCounts;
    // NOTE: DO NOT add data as direct dependency. It will render infinitely.
  }, [configsOptions, data.length]);
  const getConfigFilters = useCallback(
    (prevState?: FilterType[]): FilterType[] =>
      compact(
        filtersEntries.map((filter, index) => {
          const {
            options = [],
            optionsWithCount = {},
            values = [],
          } = filtersWithOptionsAndCounts[filter.entryCode] || {};
          const prevStateFilter = prevState?.find(
            (_filter) => _filter.category === filter.entryCode,
          );
          let defaultDateSelectedValue = '';
          if (filter.referenceObject?.functionName === ConfigEntryRefFuncName.INSIGHTS_DATE) {
            defaultDateSelectedValue =
              INSIGHTS_DATE_OPTIONS.find((option) => option.value === 'All')?.value || '';
          }
          return (
            (!filter.referenceObject?.roles?.length ||
              user?.roles?.some((role) =>
                filter.referenceObject?.roles?.includes(role.roleName),
              )) && {
              ...prevStateFilter,
              category: filter.entryCode,
              categoryIndex: index,
              displayName: filter.referenceObject?.label || filter.entryDescription,
              options:
                options.map((option) => ({
                  label: option.label,
                  value: option.value,
                  ...(showFiltersCount && {
                    badgeValue: optionsWithCount[option.value] || 0,
                  }),
                })) || [],
              allOptions: values || [],
              showSelectAll,
              defaultOpen: false,
              selectedOptions: prevStateFilter?.selectedOptions || [],
              ...(filter.referenceObject?.type === ConfigEntryFieldType.SELECT ||
              filter.referenceObject?.type === ConfigEntryFieldType.RADIO
                ? {
                    inputType: 'radio',
                    selectedValue: prevStateFilter?.selectedValue || defaultDateSelectedValue || '',
                    defaultSelectedValue: defaultDateSelectedValue,
                  }
                : { inputType: 'checkbox' }),
            }
          );
        }),
      ),
    [filtersWithOptionsAndCounts, filtersEntries],
  );
  const getFilters = useCallback(
    (prevState?: FilterType[]): FilterType[] =>
      getAdditionalFilters?.({ configFilters: getConfigFilters(prevState), prevState }) ||
      getConfigFilters(prevState),
    [getAdditionalFilters, getConfigFilters],
  );
  const [filters, setFilters] = useState<FilterType[]>(getFilters());

  useEffect(() => {
    setFilters((prevState) =>
      getFilters(prevState)
        .filter((filter) => filter.options.length > 0)
        .map((filter, index) => ({ ...filter, categoryIndex: index })),
    );
  }, [getFilters]);

  const clearFilters = useCallback((options?: ClearFiltersOptions) => {
    if (options?.defaultClearFiltersCallback) {
      options.defaultClearFiltersCallback?.();
    } else {
      setFilters((prevState) => {
        return prevState.map((filter) => ({
          ...filter,
          selectedOptions: [],
          selectedValue: filter.defaultSelectedValue,
          defaultSelectedValue: filter.defaultSelectedValue,
        }));
      });
    }
  }, []);

  return {
    filters,
    isFiltersLoading: isFiltersLoading || isLoading,
    clearFilters,
    setFilters,
  };
}

export default useConfigFilters;
